import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Phone, GraduationCap, MapPin, Award } from "lucide-react";
import { contacts } from "../constants/contacts";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Academics", path: "/academics" },
    { name: "Admissions", path: "/admissions" },
    { name: "At a Glance", path: "/school-at-a-glance" },
    { name: "Activities", path: "/activities" },
  ];

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-slate-200">
      {/* Top Announcement & Quick Contact Bar */}
      <div className="bg-slate-100 text-slate-700 text-xs py-1.5 px-4 sm:px-6 lg:px-8 border-b border-slate-200 hidden sm:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 text-[11px] font-medium text-slate-600">
            <span className="flex items-center gap-1 text-primary font-bold">
              <Award className="w-3.5 h-3.5 text-amber-500" /> Best boarding school of the year
            </span>
            <span className="text-slate-300">|</span>
            <span className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5 text-slate-400" /> Dumka, Jharkhand
            </span>
          </div>

          <div className="flex items-center space-x-4 text-[11px]">
            <a
              href={`tel:${contacts.mobile1}`}
              className="flex items-center gap-1 text-slate-700 hover:text-primary transition-colors font-medium"
            >
              <Phone className="w-3 h-3 text-primary" />
              <span>{contacts.mobile1}</span>
            </a>
            <span className="text-slate-300">|</span>
            <a
              href={`https://wa.me/${contacts.whatsApp}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-emerald-700 hover:text-emerald-800 transition-colors font-medium"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="text-emerald-600"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              <span>WhatsApp Support</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Executive Logo & School Identity Branding */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white p-1 rounded-xl border border-slate-200 shadow-xs flex items-center justify-center shrink-0 group-hover:border-primary transition-colors">
              <img
                className="h-full w-auto object-contain"
                src="/assets/images/logo.png"
                alt="Dee Novo School Logo"
              />
            </div>

            <div className="flex flex-col">
              <span className="text-lg sm:text-xl md:text-2xl font-black text-primary tracking-tight leading-tight group-hover:text-primaryDark transition-colors">
                DEE NOVO SCHOOL
              </span>
              <span className="text-[10px] sm:text-[11px] font-semibold text-slate-500 tracking-wider uppercase">
                Excellence in Education • CBSE Affiliated
              </span>
            </div>
          </Link>

          {/* Desktop Navigation Links & Action */}
          <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
            <nav className="flex space-x-6 lg:space-x-8">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`font-semibold text-sm lg:text-base transition-all duration-200 py-1 relative ${
                      isActive
                        ? "text-primary font-bold"
                        : "text-slate-600 hover:text-primary"
                    }`}
                  >
                    {link.name}
                    {isActive && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full"></span>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Apply Button */}
            <Link
              to="/admissions"
              className="px-5 py-2 bg-primary hover:bg-primaryDark text-white font-bold rounded-lg shadow-xs transition-colors text-sm flex items-center gap-1.5"
            >
              <GraduationCap className="w-4 h-4" /> Apply Now
            </Link>
          </div>

          {/* Mobile Toggle & Quick Actions */}
          <div className="flex md:hidden items-center gap-2">
            <a
              href={`tel:${contacts.mobile1}`}
              className="w-9 h-9 flex items-center justify-center bg-blue-50 text-primary rounded-lg border border-blue-100"
              aria-label="Call Us"
            >
              <Phone className="w-4 h-4" />
            </a>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-slate-700 hover:text-primary focus:outline-none transition-colors"
              aria-label="Toggle Menu"
            >
              {isOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
          className="md:hidden bg-white border-t border-slate-200 shadow-md"
        >
          <div className="px-4 py-3 space-y-2">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    isActive
                      ? "bg-blue-50 text-primary"
                      : "text-slate-700 hover:bg-slate-50 hover:text-primary"
                  }`}
                >
                  {link.name}
                </Link>
              );
            })}

            <div className="pt-2">
              <Link
                to="/admissions"
                onClick={() => setIsOpen(false)}
                className="block w-full text-center px-4 py-2.5 bg-primary text-white font-bold rounded-lg shadow-xs text-sm"
              >
                Apply Now
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </header>
  );
};

export default Header;
